import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { UserPlus, ArrowLeft } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import SEOHead from "@/components/SEOHead";

export default function PatientRegister() {
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", dob: "", password: "" });
  const [loading, setLoading] = useState(false);
  const { setUser } = { setUser: () => {} }; // placeholder, we refetch via /me on next mount

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/patient/register", form);
      toast.success("¡Bienvenido/a! Tu cuenta ha sido creada.");
      // Force reload so AuthContext picks up the new session
      window.location.href = "/paciente/panel";
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <>
      <SEOHead title="Portal del paciente · Registro" description="Crea tu cuenta para gestionar tus citas y tu historial médico con Katia Falón Odontología." path="/paciente/registro" />
      <section className="mx-auto max-w-lg px-6 py-16 lg:py-24" data-testid="patient-register-page">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-[hsl(var(--clinic-muted))]"><ArrowLeft size={14} /> Volver al inicio</Link>
        <div className="mt-6 rounded-3xl border border-[hsl(var(--clinic-border))] bg-white p-8">
          <div className="text-xs uppercase tracking-[0.3em] text-[hsl(var(--clinic-muted))]">Portal del paciente</div>
          <h1 className="mt-1 font-display text-3xl">Crear cuenta</h1>
          <p className="mt-2 text-sm text-[hsl(var(--clinic-muted))]">
            Gestiona tus citas, revisa tu historial y conserva tu ficha médica siempre a mano.
          </p>
          <form onSubmit={submit} data-testid="patient-register-form" className="mt-6 grid gap-4">
            <Field label="Nombre completo" required>
              <input required data-testid="pr-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" />
            </Field>
            <Field label="Correo" required>
              <input required type="email" data-testid="pr-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" />
            </Field>
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Teléfono / WhatsApp" required>
                <input required data-testid="pr-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" />
              </Field>
              <Field label="Fecha de nacimiento">
                <input type="date" data-testid="pr-dob" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="input" />
              </Field>
            </div>
            <Field label="Contraseña (mín. 8 caracteres)" required>
              <input required type="password" minLength={8} data-testid="pr-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="input" />
            </Field>
            <button type="submit" disabled={loading} data-testid="pr-submit" className="inline-flex items-center justify-center gap-2 rounded-full bg-[hsl(var(--clinic-secondary))] px-6 py-3 text-sm font-semibold text-white disabled:opacity-60">
              <UserPlus size={16} /> {loading ? "Creando cuenta…" : "Crear cuenta"}
            </button>
          </form>
          <p className="mt-4 text-sm text-[hsl(var(--clinic-muted))]">
            ¿Ya tienes cuenta? <Link to="/paciente/login" data-testid="pr-login-link" className="font-semibold text-[hsl(var(--clinic-secondary))] underline">Entra</Link>
          </p>
        </div>
        <style>{`.input{width:100%;border:1px solid hsl(var(--clinic-border));border-radius:0.75rem;padding:0.75rem 1rem;font-size:0.9rem;background:white}.input:focus{outline:none;border-color:hsl(var(--clinic-primary));box-shadow:0 0 0 3px hsl(var(--clinic-primary)/0.15)}`}</style>
      </section>
    </>
  );
}
function Field({ label, children, required }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--clinic-muted))]">{label} {required && <span className="text-[hsl(var(--clinic-secondary))]">*</span>}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
